import { FormPreviewRAM } from "@/contexts/fiscalizacao/relatorioAM/components/FormPreviewRAM/FormPreviewRAM";
import { FormRemocaoParecerRAM } from "@/contexts/fiscalizacao/relatorioAM/components/FormRemocaoParecerRAM/FormRemocaoParecerRAM";
import { ModalPlanoAcaoRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/components/ModalHistoricoPlanoAcaoRAM/ModalPlanoAcaoRelatorioAM";
import { useRelatorioAM } from "@/contexts/fiscalizacao/relatorioAM/hooks/useRelatorioAM";
import { PageLoader } from "@/shared/components/PageLoader/PageLoader";
import { useNetworkStatus } from "@/shared/hooks/useNetworkStatus";
import { ArrowLeftOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Col,
  Collapse,
  Divider,
  Grid,
  Result,
  Row,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";

const ID_ALTERNATIVA_NAO_CONFORME = 2;

export function RemocaoParecerRelatorioAM() {
  const { isOnline } = useNetworkStatus();
  const { relatorioId } = useParams<{ relatorioId: string }>();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const { getRelatorioAMByIdApi, canMakeParecer } = useRelatorioAM();
  const [isOpenModalHistorico, setIsOpenModalHistorico] = useState(false);
  const [itemIdSelecionado, setItemIdSelecionado] = useState<number | null>(
    null
  );

  const { data: relatorio, isLoading: loadingRelatorio } = useQuery({
    queryKey: ["RELATORIO_AM_BY_ID", relatorioId],
    enabled: isOnline && !!relatorioId,
    queryFn: async () => {
      const response = await getRelatorioAMByIdApi(Number(relatorioId));
      return response;
    },
  });

  const itensNaoConformes = useMemo(() => {
    if (!relatorio?.itens) return [];
    return relatorio.itens.filter(
      (item) =>
        item.alternativa?.id === ID_ALTERNATIVA_NAO_CONFORME &&
        (item.status === "AGUARDANDO_PLANO_DE_ACAO" ||
          item.status === "REGULARIZADO")
    );
  }, [relatorio]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleOpenHistoricoPlanoAcao = (itemId: number) => {
    setItemIdSelecionado(itemId);
    setIsOpenModalHistorico(true);
  };

  const handleCloseHistoricoPlanoAcao = () => {
    setIsOpenModalHistorico(false);
  };

  useEffect(() => {
    document.title =
      "Inspeção Mobile - Remover Parecer Relatórios de Auditoria e Monitoria";
  }, []);

  if (!isOnline) {
    return <Result title="Sem conexão com a internet" status={"error"} />;
  }

  if (loadingRelatorio) {
    return <PageLoader />;
  }

  if (!relatorio) {
    return <Result title="Relatório não encontrado" status={"error"} />;
  }

  const userCanMakeParecer = canMakeParecer(
    relatorio.formulario?.tipoFormulario,
    relatorio.estabelecimento?.id
  );

  // if (!userCanMakeParecer) {
  //   return <Navigate to="/" />;
  // }

  return (
    <>
      <Typography.Title
        level={screens.xs ? 3 : 4}
        style={{ textAlign: "center" }}
      >
        Remover Parecer Relatorio de Auditoria e Monitoria {relatorio?.id}
      </Typography.Title>
      <Divider />

      <FormPreviewRAM relatorio={relatorio} />

      <Divider orientation="left">
        <Typography.Title level={5}>Parecer</Typography.Title>
      </Divider>

      {itensNaoConformes.length === 0 && (
        <Typography.Text type="secondary">
          Nenhuma pergunta com item não conforme aguardando plano de ação
          encontrada.
        </Typography.Text>
      )}

      {itensNaoConformes.length > 0 && (
        <Collapse
          accordion
          expandIconPosition="end"
          items={itensNaoConformes.map((item) => ({
            key: item.uuid,
            label: item.pergunta?.nome,
            children: (
              <Row gutter={[8, 8]}>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Seção:</strong> {item.secao?.nome}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Data Resposta:</strong>{" "}
                    {dayjs(item.dataResposta).format("DD/MM/YYYY")}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="danger">
                    <strong>Alternativa: </strong>
                    {item.alternativa?.nome}
                  </Typography.Text>
                </Col>
                <Col xs={24} sm={12}>
                  <Typography.Text type="secondary">
                    <strong>Observação:</strong> {item.resposta || "-"}
                  </Typography.Text>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    icon={<UnorderedListOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenHistoricoPlanoAcao(item.id!);
                    }}
                  >
                    Histórico
                  </Button>
                </Col>

                <Col xs={24}>
                  <FormRemocaoParecerRAM item={item} />
                </Col>
              </Row>
            ),
          }))}
        />
      )}

      <Divider />

      <Row justify="center" gutter={[10, 10]}>
        <Col>
          <Button
            htmlType="button"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ width: 200 }}
          >
            Voltar
          </Button>
        </Col>
      </Row>

      <ModalPlanoAcaoRelatorioAM
        open={isOpenModalHistorico}
        onClose={handleCloseHistoricoPlanoAcao}
        ramItemId={itemIdSelecionado!}
      />
    </>
  );
}
